'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, CheckCircle2, CreditCard, ShieldCheck, Store, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface StepCompleteProps {
    businessName: string;
}

export function StepComplete({ businessName }: StepCompleteProps) {
    const router = useRouter();
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        if (countdown <= 0) {
            router.replace('/merchant/dashboard');
            return;
        }
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown, router]);

    return (
        <div className="py-2">
            {/* Success mark */}
            <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-8 ring-emerald-50/60">
                    <CheckCircle2 className="h-8 w-8" />
                </div>

                <h3 className="mt-5 text-xl font-semibold tracking-tight text-slate-950">
                    Your workspace is ready
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                    Welcome,{' '}
                    <strong className="font-semibold text-slate-800">{businessName}</strong>.
                    Your merchant dashboard is configured and waiting.
                </p>
            </div>

            {/* What's ready */}
            <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                    { icon: Store,       label: 'Business profile', note: 'Ready to customise' },
                    { icon: Users,       label: 'Staff access',     note: 'Invite your team'   },
                    { icon: CreditCard,  label: 'POS checkout',     note: 'Start selling'       },
                    { icon: ShieldCheck, label: 'Owner account',    note: 'Secured'             },
                ].map((item) => (
                    <div
                        key={item.label}
                        className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3.5"
                    >
                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                            <item.icon className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                            <p className="mt-0.5 text-xs text-slate-400">{item.note}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-col items-center gap-3">
                <Button
                    onClick={() => router.replace('/merchant/dashboard')}
                    className="h-11 w-full rounded-xl bg-violet-600 text-sm font-semibold text-white shadow-sm hover:bg-violet-700"
                >
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <p className="text-xs text-slate-400">
                    Redirecting automatically in{' '}
                    <span className="tabular-nums font-medium text-slate-600">{countdown}s</span>
                </p>
            </div>
        </div>
    );
}